import React, { Dispatch } from 'react';
import fetchResults from '~/core/api/fetchResults';
import getNewRequestParams from '~/core/api/getNewRequestParams';
import { ResponseData } from '~/core/api/ResponseTypes';
import { Persistence as Persistence_T } from '~/core/persistence/Persistence';
import countResults from '~/utils/countResults';
import SRError from '~/utils/errors/srError';
import { SearchEventParams } from '../events/EventFactory';
import { ResetQueryEvent } from '../events/types/eventTypes/ResetQueryEvent';
import { SearchEvent } from '../events/types/eventTypes/SearchEvent';
import { SERPEvent } from '../events/types/SERPEvents';
import { SearchAction } from '../state/actions';
import { State } from '../state/types/state';

export type FetchResultsEffectOptions = {
    emitSearchEvent: boolean;
};

export type FetchResultsEffectParams = {
    dispatch: Dispatch<SearchAction>;
    isInitialRender: boolean;
    searchQuery: State['searchQuery'];
    sets: State['sets'];
    controls: State['controls'];
    initialPagination: State['initialPagination'];
    isAfterReset: React.RefObject<boolean>;
    setIsAfterReset: (value: boolean) => void;
    // in the future we need to separate instant search state from general application state, so we don't have to pass
    // any payload to resetState.
    resetState: (payload?: { exceptInstantSearch?: boolean }) => void;
    Persistence: Persistence_T;
    apiUrl: string;
    searchSuccess: (results: ResponseData) => void;
    emitEvent: (event: SERPEvent) => void;
    createSearchEvent: (searchEventParams: SearchEventParams) => SearchEvent;
    createResetQueryEvent: () => ResetQueryEvent;
    updateError: (error: SRError) => void;
    abortController: AbortController;
    isDebugMode: boolean;
    isInstantSearch: boolean;
};

/**
 * Effect used for fetching search results from API.
 *
 * @remarks
 * Calls the {@link fetchResults} function.
 */
export const fetchResultsEffect = (
    {
        dispatch,
        isInitialRender,
        initialPagination,
        searchQuery,
        /** obtained from {@link useReset} hook */
        isAfterReset,
        /** obtained from {@link useReset} hook */
        setIsAfterReset,
        /** obtained from {@link useReset} hook */
        resetState,
        Persistence,
        apiUrl,
        sets,
        controls,
        searchSuccess,
        emitEvent,
        createSearchEvent,
        createResetQueryEvent,
        updateError,
        abortController,
        isDebugMode,
        isInstantSearch,
    }: FetchResultsEffectParams,
    { emitSearchEvent }: FetchResultsEffectOptions
) => {
    if (isInitialRender && searchQuery === '') {
        return;
    }

    if (searchQuery.length < 2 && !isAfterReset.current) {
        resetState({ exceptInstantSearch: true });
        Persistence.clearStorage();
        emitEvent(createResetQueryEvent());
        return;
    }

    if (isAfterReset.current) {
        setIsAfterReset(false);
        return;
    }

    (async () => {
        dispatch({
            type: 'start-loading',
        });
        await fetchResults({
            apiUrl,
            searchQuery,
            parameters: getNewRequestParams(sets, controls, initialPagination),
            // eslint-disable-next-line @typescript-eslint/no-shadow
            successCallback: ({ data, searchQuery, requestParams }) => {
                if (!isInstantSearch) {
                    Persistence.updateStorage({ searchQuery, controlsState: controls });
                }
                searchSuccess(data);

                if (emitSearchEvent) {
                    emitEvent(
                        createSearchEvent({
                            searchQuery,
                            requestParams,
                            totalResultsCount: countResults(data.results),
                            results: data.results,
                            serpType: isInstantSearch ? 'instant' : 'full',
                        })
                    );
                }
            },
            failedCallback: updateError,
            abortController,
            isDebugMode,
        });
    })();
};
